import { JobTitleEnum } from '@pflegia/types/api/job-title';

export const MAX_LLP_FAQS = 5;

export type LlpFaqPageCase = 'jobAndLocation' | 'positionOnly' | 'locationOnly';

export type LlpFaqLinkIntent = 'salary' | 'profession' | 'training' | 'furtherEducation' | 'similarPosition';

export type LlpFaqCategory = 'salary' | 'availableJobs' | 'employers' | 'professionOrTraining' | 'jobSpecific';

export interface LlpFaqData {
  pageCase: LlpFaqPageCase;
  jobTitleKey?: JobTitleEnum;
  hasSalary?: boolean;
  salaryUsedFallback?: boolean;
  hasVacancyCount?: boolean;
  employerNames?: string[];
  relatedLocations?: string[];
  links?: Partial<Record<LlpFaqLinkIntent, string>>;
}

export interface SelectedLlpFaq {
  category: LlpFaqCategory;
  link?: string;
  linkIntent?: LlpFaqLinkIntent;
  salaryUsedFallback?: boolean;
}

export const SPECIAL_QUESTION_JOB_TITLES = new Set<JobTitleEnum>([
  JobTitleEnum.HygieneSpecialist,
  JobTitleEnum.Companion,
  JobTitleEnum.AssistantElderlyCareNurse,
  JobTitleEnum.UnitManager,
  JobTitleEnum.NursingSupervisor,
  JobTitleEnum.Nurse,
  JobTitleEnum.Midwife,
  JobTitleEnum.AnestheticAssistant,
  JobTitleEnum.OperationAssistant,
  JobTitleEnum.MedicalAssistant,
]);

const PAGE_CASE_CATEGORIES: Record<LlpFaqPageCase, LlpFaqCategory[]> = {
  jobAndLocation: ['salary', 'availableJobs', 'employers', 'professionOrTraining', 'jobSpecific'],
  positionOnly: ['salary', 'professionOrTraining', 'jobSpecific'],
  locationOnly: ['salary', 'availableJobs', 'employers'],
};

const buildFaq = (category: LlpFaqCategory, data: LlpFaqData): SelectedLlpFaq | undefined => {
  const links = data.links ?? {};

  switch (category) {
    case 'salary':
      return data.hasSalary && links.salary
        ? { category, link: links.salary, salaryUsedFallback: Boolean(data.salaryUsedFallback) }
        : undefined;
    case 'availableJobs':
      return data.hasVacancyCount ? { category } : undefined;
    case 'employers':
      return (data.employerNames?.length ?? 0) > 0 ? { category } : undefined;
    case 'professionOrTraining': {
      const intent = (['profession', 'training', 'furtherEducation'] as const).find(key => links[key]);
      return intent ? { category, link: links[intent], linkIntent: intent } : undefined;
    }
    case 'jobSpecific':
      return data.jobTitleKey && SPECIAL_QUESTION_JOB_TITLES.has(data.jobTitleKey) ? { category } : undefined;
  }
};

export const selectLlpFaqs = (data: LlpFaqData): SelectedLlpFaq[] =>
  PAGE_CASE_CATEGORIES[data.pageCase]
    .map(category => buildFaq(category, data))
    .filter((faq): faq is SelectedLlpFaq => faq !== undefined)
    .slice(0, MAX_LLP_FAQS);
